import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { requisitionsAPI, boqAPI, jobsAPI, usersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format } from 'date-fns';
import JobSearchSelect from '../components/JobSearchSelect';
import {
  Package,
  Plus,
  ArrowLeft,
  FileText,
  CheckCircle2,
  Clock,
  User,
  Trash2,
  Edit,
  Eye,
  X,
  ShoppingCart,
  AlertTriangle,
  TruckIcon,
  Save,
  LayoutGrid,
  List,
  Search,
} from 'lucide-react';

const Requisitions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobIdParam = searchParams.get('jobId');
  const boqIdParam = searchParams.get('boqId');
  const { user } = useAuth();
  
  const [requisitions, setRequisitions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [createData, setCreateData] = useState({
    job_id: jobIdParam || '',
    boq_id: boqIdParam || '',
    items: [],
    notes: '',
  });
  const [partialFulfillmentNote, setPartialFulfillmentNote] = useState('');
  const [editingItems, setEditingItems] = useState([]);
  const [selectedCreateItems, setSelectedCreateItems] = useState(new Set());

  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  const isBuyer = user?.role === 'admin' || user?.role === 'manager';

  // Toggle select all for create form
  const toggleSelectAllCreateItems = () => {
    if (selectedCreateItems.size === createData.items.length) {
      setSelectedCreateItems(new Set());
    } else {
      setSelectedCreateItems(new Set(createData.items.map(item => item.id)));
    }
  };

  // Toggle single item in create form
  const toggleSelectCreateItem = (itemId) => {
    const newSelected = new Set(selectedCreateItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedCreateItems(newSelected);
  };

  // Delete selected items in create form
  const deleteSelectedCreateItems = () => {
    if (selectedCreateItems.size === 0) return;
    if (!window.confirm(`Are you sure you want to remove ${selectedCreateItems.size} item(s)?`)) return;
    
    setCreateData(prev => ({
      ...prev,
      items: prev.items.filter(item => !selectedCreateItems.has(item.id))
    }));
    setSelectedCreateItems(new Set());
    toast.success(`${selectedCreateItems.size} item(s) removed`);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (createData.boq_id) {
      loadBOQItems();
    }
  }, [createData.boq_id]);

  const loadData = async () => {
    try {
      const [reqRes, jobsRes, managersRes] = await Promise.all([
        requisitionsAPI.getAll(),
        jobsAPI.getAll(),
        usersAPI.getManagers(),
      ]);
      setRequisitions(reqRes.data);
      setJobs(jobsRes.data);
      setManagers(managersRes.data);
      
      if (jobIdParam && boqIdParam) {
        setShowCreateModal(true);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadBOQItems = async () => {
    try {
      const response = await boqAPI.getOne(createData.boq_id);
      if (response.data?.line_items) {
        // Pre-populate requisition items from BOQ
        setCreateData(prev => ({
          ...prev,
          items: response.data.line_items.map(item => ({
            id: item.id,
            category: item.category || 'Materials',
            item_description: item.item_description,
            quantity: item.quantity,
            unit: item.unit,
            estimated_rate: item.rate,
            estimated_amount: item.amount,
            fulfilled_quantity: 0,
            supplier: '',
            status: 'pending',
          })),
        }));
      }
    } catch (error) {
      console.error('Failed to load BOQ items');
    }
  };

  const handleCreateRequisition = async () => {
    if (!createData.job_id || !createData.boq_id) {
      toast.error('Please select a job and BOQ');
      return;
    }

    if (createData.items.length === 0) {
      toast.error('No items to requisition');
      return;
    }

    try {
      await requisitionsAPI.create({
        job_id: createData.job_id,
        boq_id: createData.boq_id,
        requisition_items: createData.items,
        notes: createData.notes,
      });
      toast.success('Requisition created successfully');
      setShowCreateModal(false);
      setCreateData({ job_id: '', boq_id: '', items: [], notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to create requisition');
    }
  };

  const handleUpdateStatus = async (reqId, newStatus) => {
    try {
      await requisitionsAPI.update(reqId, { status: newStatus });
      toast.success(`Requisition ${newStatus.replace('_', ' ')}`);
      loadData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to update requisition');
    }
  };

  const handleOpenPartialFulfillment = (req) => {
    setSelectedRequisition(req);
    setEditingItems(req.requisition_items?.map(item => ({
      ...item,
      fulfilled_quantity: item.fulfilled_quantity || 0,
    })) || []);
    setPartialFulfillmentNote(req.partial_fulfillment_note || '');
    setShowPartialModal(true);
  };

  const handleUpdateItemFulfillment = (index, value) => {
    const updated = [...editingItems];
    const maxQty = updated[index].quantity;
    updated[index].fulfilled_quantity = Math.min(Math.max(0, value), maxQty);
    
    // Auto-update status based on fulfillment
    if (updated[index].fulfilled_quantity === 0) {
      updated[index].status = 'pending';
    } else if (updated[index].fulfilled_quantity < updated[index].quantity) {
      updated[index].status = 'partial_fulfilled';
    } else {
      updated[index].status = 'fulfilled';
    }
    
    setEditingItems(updated);
  };

  const handleSavePartialFulfillment = async () => {
    if (!selectedRequisition) return;

    // Check if any partial fulfillment
    const hasPartial = editingItems.some(item => 
      item.fulfilled_quantity > 0 && item.fulfilled_quantity < item.quantity
    );
    
    if (hasPartial && !partialFulfillmentNote.trim()) {
      toast.error('Please provide a note explaining the partial fulfillment');
      return;
    }

    // Determine overall status
    const totalQty = editingItems.reduce((sum, item) => sum + item.quantity, 0);
    const fulfilledQty = editingItems.reduce((sum, item) => sum + (item.fulfilled_quantity || 0), 0);
    
    let newStatus = selectedRequisition.status;
    if (fulfilledQty === 0) {
      newStatus = 'ordered';
    } else if (fulfilledQty < totalQty) {
      newStatus = 'partial_fulfilled';
    } else {
      newStatus = 'delivered';
    }

    try {
      await requisitionsAPI.update(selectedRequisition.id, {
        requisition_items: editingItems,
        partial_fulfillment_note: partialFulfillmentNote || null,
        status: newStatus,
      });
      toast.success('Fulfillment updated successfully');
      setShowPartialModal(false);
      setPartialFulfillmentNote('');
      loadData();
    } catch (error) {
      toast.error('Failed to update fulfillment');
    }
  };

  // Add item to existing requisition
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newItem, setNewItem] = useState({
    category: 'Materials',
    item_description: '',
    quantity: 1,
    unit: 'each',
    estimated_rate: 0,
  });

  const handleAddItemToRequisition = async () => {
    if (!selectedRequisition) return;
    if (!newItem.item_description.trim()) {
      toast.error('Please enter an item description');
      return;
    }

    try {
      await requisitionsAPI.addItem(selectedRequisition.id, {
        ...newItem,
        estimated_amount: newItem.quantity * newItem.estimated_rate,
      });
      toast.success('Item added successfully');
      setShowAddItemModal(false);
      setNewItem({
        category: 'Materials',
        item_description: '',
        quantity: 1,
        unit: 'each',
        estimated_rate: 0,
      });
      loadData();
      // Refresh selected requisition
      const res = await requisitionsAPI.getOne(selectedRequisition.id);
      setSelectedRequisition(res.data);
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleDeleteRequisitionItem = async (itemId, itemStatus) => {
    if (!selectedRequisition) return;
    if (itemStatus === 'fulfilled' || itemStatus === 'ordered') {
      toast.error('Cannot delete fulfilled or ordered items');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await requisitionsAPI.deleteItem(selectedRequisition.id, itemId);
      toast.success('Item deleted successfully');
      loadData();
      // Refresh selected requisition
      const res = await requisitionsAPI.getOne(selectedRequisition.id);
      setSelectedRequisition(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  const statusColors = {
    pending_quotes: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    quotes_received: 'bg-blue-100 text-blue-700 border-blue-200',
    approved: 'bg-green-100 text-green-700 border-green-200',
    ordered: 'bg-purple-100 text-purple-700 border-purple-200',
    partial_fulfilled: 'bg-orange-100 text-orange-700 border-orange-200',
    delivered: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const getJobInfo = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? { code: job.job_code, title: job.title } : { code: '', title: 'Unknown Job' };
  };

  const calculateFulfillmentPercentage = (req) => {
    if (!req.requisition_items?.length) return 0;
    const total = req.requisition_items.reduce((sum, item) => sum + item.quantity, 0);
    const fulfilled = req.requisition_items.reduce((sum, item) => sum + (item.fulfilled_quantity || 0), 0);
    return total > 0 ? Math.round((fulfilled / total) * 100) : 0;
  };

  const filteredRequisitions = requisitions.filter(req => {
    const jobInfo = getJobInfo(req.job_id);
    return (
      jobInfo.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      jobInfo.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Requisitions
          </h1>
          <p className="text-slate-600">Manage material requisitions for jobs</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Requisition
          </button>
        )}
      </div>

      {/* Search and View Toggle */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by job name or code..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              title="Grid View"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Requisitions Display */}
      {filteredRequisitions.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No requisitions found</h3>
          <p className="text-slate-600 mb-4">Create a requisition from a BOQ to get started.</p>
          {canEdit && (
            <button
              onClick={() => navigate('/boq')}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FileText className="w-5 h-5" />
              Go to BOQ Management
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRequisitions.map((req) => {
            const jobInfo = getJobInfo(req.job_id);
            const fulfillmentPct = calculateFulfillmentPercentage(req);
            return (
              <div key={req.id} className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    {jobInfo.code && (
                      <span className="text-xs font-mono font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                        {jobInfo.code}
                      </span>
                    )}
                    <h3 className="font-semibold text-slate-900 mt-2">{jobInfo.title}</h3>
                    {req.auto_generated && (
                      <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Auto-generated</span>
                    )}
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[req.status]}`}>
                    {req.status.replace(/_/g, ' ')}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Items:</span>
                    <span className="font-medium text-slate-900">{req.requisition_items?.length || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Estimated:</span>
                    <span className="font-medium text-slate-900">{formatCurrency(req.total_estimated || 0)}</span>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Fulfillment:</span>
                      <span className="font-medium text-slate-900">{fulfillmentPct}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${fulfillmentPct === 100 ? 'bg-green-500' : fulfillmentPct > 0 ? 'bg-orange-500' : 'bg-slate-300'}`}
                        style={{ width: `${fulfillmentPct}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                    Created {format(new Date(req.created_at), 'MMM dd, yyyy')}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
                  <button
                    onClick={() => { setSelectedRequisition(req); setShowDetailModal(true); }}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  {isBuyer && req.status !== 'delivered' && (
                    <button
                      onClick={() => handleOpenPartialFulfillment(req)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors text-sm"
                    >
                      <TruckIcon className="w-4 h-4" />
                      Fulfill
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Job</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Items</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Estimated Total</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Fulfillment</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Created</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredRequisitions.map((req) => {
                const jobInfo = getJobInfo(req.job_id);
                const fulfillmentPct = calculateFulfillmentPercentage(req);
                return (
                  <tr key={req.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        {jobInfo.code && (
                          <span className="text-xs font-mono font-bold text-blue-600 mr-2">
                            {jobInfo.code}
                          </span>
                        )}
                        <span className="font-medium text-slate-900">{jobInfo.title}</span>
                        {req.auto_generated && (
                          <span className="ml-2 text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                            Auto-generated
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {req.requisition_items?.length || 0} items
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {formatCurrency(req.total_estimated || 0)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden max-w-[80px]">
                          <div 
                            className={`h-full ${fulfillmentPct === 100 ? 'bg-green-500' : fulfillmentPct > 0 ? 'bg-orange-500' : 'bg-slate-300'}`}
                            style={{ width: `${fulfillmentPct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-600">{fulfillmentPct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[req.status]}`}>
                        {req.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-sm">
                      {format(new Date(req.created_at), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setSelectedRequisition(req); setShowDetailModal(true); }}
                          className="p-2 hover:bg-slate-100 rounded transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-slate-600" />
                        </button>
                        {isBuyer && req.status !== 'delivered' && (
                          <button
                            onClick={() => handleOpenPartialFulfillment(req)}
                            className="p-2 hover:bg-slate-100 rounded transition-colors"
                            title="Update Fulfillment"
                          >
                            <TruckIcon className="w-4 h-4 text-slate-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Create Requisition
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Job *</label>
                  <JobSearchSelect
                    jobs={jobs}
                    selectedJobId={createData.job_id}
                    onSelect={(jobId) => setCreateData({ ...createData, job_id: jobId, boq_id: '' })}
                    placeholder="Search for a job..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select BOQ *</label>
                  <select
                    value={createData.boq_id}
                    onChange={(e) => setCreateData({ ...createData, boq_id: e.target.value })}
                    disabled={!createData.job_id}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                  >
                    <option value="">Choose BOQ...</option>
                    {createData.job_id && (
                      <option value={boqIdParam || 'auto'}>Job BOQ</option>
                    )}
                  </select>
                </div>
              </div>

              {createData.items.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700">Requisition Items</h3>
                    {selectedCreateItems.size > 0 && (
                      <button
                        onClick={deleteSelectedCreateItems}
                        className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove {selectedCreateItems.size} item(s)
                      </button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 w-10">
                            <input
                              type="checkbox"
                              checked={selectedCreateItems.size === createData.items.length && createData.items.length > 0}
                              onChange={toggleSelectAllCreateItems}
                              className="rounded border-slate-300"
                            />
                          </th>
                          <th className="text-left px-3 py-2">Category</th>
                          <th className="text-left px-3 py-2">Item</th>
                          <th className="text-left px-3 py-2">Qty</th>
                          <th className="text-left px-3 py-2">Unit</th>
                          <th className="text-left px-3 py-2">Est. Rate</th>
                          <th className="text-left px-3 py-2">Est. Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {createData.items.map((item, index) => (
                          <tr key={index} className={selectedCreateItems.has(item.id) ? 'bg-blue-50' : ''}>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedCreateItems.has(item.id)}
                                onChange={() => toggleSelectCreateItem(item.id)}
                                className="rounded border-slate-300"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{item.category}</span>
                            </td>
                            <td className="px-3 py-2">{item.item_description}</td>
                            <td className="px-3 py-2">{item.quantity}</td>
                            <td className="px-3 py-2">{item.unit}</td>
                            <td className="px-3 py-2">{formatCurrency(item.estimated_rate)}</td>
                            <td className="px-3 py-2 font-medium">{formatCurrency(item.estimated_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50">
                        <tr>
                          <td colSpan={6} className="px-3 py-2 text-right font-semibold">Total:</td>
                          <td className="px-3 py-2 font-bold">
                            {formatCurrency(createData.items.reduce((sum, item) => sum + (item.estimated_amount || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={createData.notes}
                  onChange={(e) => setCreateData({ ...createData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional notes for buyer..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRequisition}
                  disabled={!createData.job_id || createData.items.length === 0}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Create Requisition
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequisition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Requisition Details</h2>
                <p className="text-sm text-slate-600">
                  {getJobInfo(selectedRequisition.job_id).code && (
                    <span className="font-mono text-blue-600 mr-2">
                      {getJobInfo(selectedRequisition.job_id).code}
                    </span>
                  )}
                  {getJobInfo(selectedRequisition.job_id).title}
                </p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex flex-wrap items-center gap-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusColors[selectedRequisition.status]}`}>
                  {selectedRequisition.status.replace(/_/g, ' ')}
                </span>
                <span className="text-sm text-slate-600">
                  Created: {format(new Date(selectedRequisition.created_at), 'MMM dd, yyyy')}
                </span>
                {selectedRequisition.auto_generated && (
                  <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">
                    Auto-generated from BOQ
                  </span>
                )}
              </div>

              {/* Partial Fulfillment Note */}
              {selectedRequisition.partial_fulfillment_note && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-800">Partial Fulfillment Note</p>
                    <p className="text-sm text-orange-600 mt-1">{selectedRequisition.partial_fulfillment_note}</p>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-700">Items</h3>
                  {canEdit && (
                    <button
                      onClick={() => setShowAddItemModal(true)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2">Category</th>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-left px-3 py-2">Qty</th>
                        <th className="text-left px-3 py-2">Fulfilled</th>
                        <th className="text-left px-3 py-2">Unit</th>
                        <th className="text-left px-3 py-2">Est. Amount</th>
                        <th className="text-left px-3 py-2">Status</th>
                        {canEdit && <th className="text-left px-3 py-2 w-10"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedRequisition.requisition_items?.map((item, index) => {
                        const canDelete = item.status !== 'fulfilled' && item.status !== 'ordered';
                        return (
                          <tr key={index} className={!canDelete ? 'bg-slate-50 opacity-75' : ''}>
                            <td className="px-3 py-2">
                              <span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{item.category || 'Materials'}</span>
                            </td>
                            <td className="px-3 py-2">{item.item_description}</td>
                            <td className="px-3 py-2">{item.quantity}</td>
                            <td className="px-3 py-2">
                              <span className={item.fulfilled_quantity < item.quantity ? 'text-orange-600 font-medium' : 'text-green-600 font-medium'}>
                                {item.fulfilled_quantity || 0}
                              </span>
                            </td>
                            <td className="px-3 py-2">{item.unit}</td>
                            <td className="px-3 py-2">{formatCurrency(item.estimated_amount)}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.status === 'fulfilled' ? 'bg-green-100 text-green-700' : 
                                item.status === 'partial_fulfilled' ? 'bg-orange-100 text-orange-700' :
                                item.status === 'ordered' ? 'bg-purple-100 text-purple-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {item.status?.replace('_', ' ') || 'pending'}
                              </span>
                            </td>
                            {canEdit && (
                              <td className="px-3 py-2">
                                {canDelete && (
                                  <button
                                    onClick={() => handleDeleteRequisitionItem(item.id, item.status)}
                                    className="p-1 hover:bg-red-100 rounded text-red-500"
                                    title="Delete item"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td colSpan={canEdit ? 6 : 5} className="px-3 py-2 text-right font-semibold">Total Estimated:</td>
                        <td colSpan={canEdit ? 2 : 2} className="px-3 py-2 font-bold">{formatCurrency(selectedRequisition.total_estimated)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {selectedRequisition.notes && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Notes</h3>
                  <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{selectedRequisition.notes}</p>
                </div>
              )}

              {isBuyer && (
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Update Status</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleUpdateStatus(selectedRequisition.id, 'quotes_received')}
                      className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm transition-colors"
                    >
                      Quotes Received
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(selectedRequisition.id, 'approved')}
                      className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(selectedRequisition.id, 'ordered')}
                      className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm transition-colors"
                    >
                      Mark Ordered
                    </button>
                    <button
                      onClick={() => { setShowDetailModal(false); handleOpenPartialFulfillment(selectedRequisition); }}
                      className="px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm transition-colors"
                    >
                      Update Fulfillment
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(selectedRequisition.id, 'delivered')}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm transition-colors"
                    >
                      Mark Delivered
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Partial Fulfillment Modal */}
      {showPartialModal && selectedRequisition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <TruckIcon className="w-5 h-5 text-orange-600" />
                Update Fulfillment
              </h2>
              <button onClick={() => setShowPartialModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <p className="text-sm text-slate-600">
                Update the fulfilled quantity for each item. If any item is partially fulfilled, please provide a note explaining why.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-left px-3 py-2">Required Qty</th>
                      <th className="text-left px-3 py-2">Fulfilled Qty</th>
                      <th className="text-left px-3 py-2">Remaining</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {editingItems.map((item, index) => {
                      const isFulfilled = item.status === 'fulfilled';
                      return (
                        <tr key={index} className={isFulfilled ? 'bg-green-50 opacity-60' : ''}>
                          <td className="px-3 py-2">
                            <div>
                              <span className="text-xs px-2 py-0.5 bg-slate-100 rounded mr-2">{item.category || 'Materials'}</span>
                              {item.item_description}
                              {isFulfilled && (
                                <span className="ml-2 text-xs text-green-600 font-medium">(Completed)</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 font-medium">{item.quantity} {item.unit}</td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.fulfilled_quantity}
                              onChange={(e) => {
                                if (isFulfilled) return; // Prevent changes to fulfilled items
                                const value = e.target.value.replace(/[^0-9.]/g, '');
                                handleUpdateItemFulfillment(index, value ? parseFloat(value) : 0);
                              }}
                              disabled={isFulfilled}
                              className={`w-24 px-2 py-1 border rounded ${
                                isFulfilled 
                                  ? 'border-green-200 bg-green-100 text-green-700 cursor-not-allowed' 
                                  : 'border-slate-200'
                              }`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <span className={item.quantity - item.fulfilled_quantity > 0 ? 'text-orange-600 font-medium' : 'text-green-600'}>
                              {(item.quantity - item.fulfilled_quantity).toFixed(2)} {item.unit}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              item.status === 'fulfilled' ? 'bg-green-100 text-green-700' : 
                              item.status === 'partial_fulfilled' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {item.status?.replace('_', ' ') || 'pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Partial Fulfillment Note */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Partial Fulfillment Note 
                  {editingItems.some(item => item.fulfilled_quantity > 0 && item.fulfilled_quantity < item.quantity) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <textarea
                  value={partialFulfillmentNote}
                  onChange={(e) => setPartialFulfillmentNote(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Explain why items are partially fulfilled (e.g., supplier stock shortage, delivery delay, etc.)"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowPartialModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePartialFulfillment}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Fulfillment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddItemModal && selectedRequisition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-600" />
                Add Item to Requisition
              </h2>
              <button onClick={() => setShowAddItemModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Materials">Materials</option>
                  <option value="Labour">Labour</option>
                  <option value="Plant & Equipment">Plant & Equipment</option>
                  <option value="Sub-Contractor">Sub-Contractor</option>
                  <option value="Preliminaries">Preliminaries</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Item Description *</label>
                <input
                  type="text"
                  value={newItem.item_description}
                  onChange={(e) => setNewItem({ ...newItem, item_description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter item description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Quantity</label>
                  <input
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Unit</label>
                  <select
                    value={newItem.unit}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="each">Each</option>
                    <option value="m">Meter (m)</option>
                    <option value="m2">Square Meter (m²)</option>
                    <option value="m3">Cubic Meter (m³)</option>
                    <option value="kg">Kilogram (kg)</option>
                    <option value="litre">Litre</option>
                    <option value="hour">Hour</option>
                    <option value="day">Day</option>
                    <option value="item">Item</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estimated Rate (R)</label>
                <input
                  type="number"
                  value={newItem.estimated_rate}
                  onChange={(e) => setNewItem({ ...newItem, estimated_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Estimated Amount:</span>
                  <span className="font-bold text-slate-900">
                    {formatCurrency(newItem.quantity * newItem.estimated_rate)}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowAddItemModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddItemToRequisition}
                  disabled={!newItem.item_description.trim()}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requisitions;
